import { EnrollmentInsuranceLike } from '../insurance/insurance'
import { InsuranceProperties } from '../insurance/properties.insurance'
import { EnrollmentProposition } from './enrollment-proposition'
import { EnrollmentPropositionInput } from './enrollment-proposition-input'
import { PropositionPricing, PropositionPricingInput } from './proposition-pricing'

// the stay ids a saved proposition is compared on, whatever fields the page fetched
type StayComparable = {
  journey?: { id: number; mainOption: { id: number } }
  mainTrip?: { id: number }
  secondaryTrip?: { id: number } | null
  options: { option: { id: number } }[]
}

// a stay change, or any other choice of the proposition form saved on the enrollment
type PropositionComparable = StayComparable &
  Pick<EnrollmentProposition, 'familyArrangement'> & {
    preferredPlace?: { id: number } | null
    journeyTripPlace?: { id: number } | null
    insurances?: EnrollmentInsuranceLike[]
  }

export class EnrollmentPropositionProperties {
  static mapEnrollmentPropositionToInput(
    proposition: EnrollmentProposition,
    { cseCode }: { cseCode?: string } = {}
  ): EnrollmentPropositionInput {
    return {
      familyArrangement: proposition.familyArrangement,
      pricing: proposition.pricing
        ? {
            includeApplicationFees: proposition.pricing.includeApplicationFees,
            includeFamilyDiscount: proposition.pricing.includeFamilyDiscount
          }
        : undefined,
      journey: proposition.journey,
      mainTrip: proposition.mainTrip,
      secondaryTrip: proposition.secondaryTrip,
      preferredPlace: proposition.preferredPlace,
      journeyTripPlace: proposition.journeyTripPlace ?? undefined,
      insurances: InsuranceProperties.mapEnrollmentInsurancesToInput(proposition.insurances),
      options: proposition.options.map((option) => ({
        id: option.option.id,
        translations: option.option.translations
      })),
      promoCode: proposition.promoCode?.code ?? null,
      cseCode
    }
  }

  // A change of formula, country, dates, theme, option or trip: the changes that may warn the
  // parents of a confirmed stay.
  static hasStayChanged(
    proposition: StayComparable,
    input: Pick<EnrollmentPropositionInput, 'journey' | 'options' | 'mainTrip' | 'secondaryTrip'>
  ) {
    const optionIds = new Set(proposition.options.map((option) => option.option.id))

    // an undefined secondary trip or option list is not sent with the update, so it is unchanged
    return (
      input.journey?.id !== proposition.journey?.id ||
      input.journey?.mainOption.id !== proposition.journey?.mainOption.id ||
      input.mainTrip?.id !== proposition.mainTrip?.id ||
      (input.secondaryTrip !== undefined &&
        (input.secondaryTrip?.id ?? null) !== (proposition.secondaryTrip?.id ?? null)) ||
      (input.options !== undefined &&
        (input.options.length !== optionIds.size ||
          input.options.some((option) => !optionIds.has(option.id))))
    )
  }

  // A flag the form leaves undefined is not sent, and a proposition saved before the pricing row
  // existed has nothing to compare with, so both count as unchanged
  static hasPricingChanged(
    pricing: PropositionPricing | null | undefined,
    input: PropositionPricingInput | undefined
  ) {
    return (
      !!pricing &&
      !!input &&
      ((input.includeApplicationFees !== undefined &&
        input.includeApplicationFees !== pricing.includeApplicationFees) ||
        (input.includeFamilyDiscount !== undefined &&
          input.includeFamilyDiscount !== pricing.includeFamilyDiscount))
    )
  }

  // Any change of the proposition form: the stay, the family arrangement, the preferred place,
  // the departure city or the insurances. LEC is asked whether to notify the team and the parents
  // after each of them.
  static hasPropositionChanged(
    proposition: PropositionComparable,
    input: Pick<
      EnrollmentPropositionInput,
      | 'journey'
      | 'options'
      | 'mainTrip'
      | 'secondaryTrip'
      | 'familyArrangement'
      | 'preferredPlace'
      | 'journeyTripPlace'
      | 'insurances'
    >
  ) {
    // like the trips and options, a field the form leaves undefined is not sent, so it is unchanged
    return (
      this.hasStayChanged(proposition, input) ||
      (input.familyArrangement !== undefined &&
        input.familyArrangement !== proposition.familyArrangement) ||
      (input.preferredPlace !== undefined &&
        (input.preferredPlace?.id ?? null) !== (proposition.preferredPlace?.id ?? null)) ||
      (input.journeyTripPlace !== undefined &&
        (input.journeyTripPlace?.id ?? null) !== (proposition.journeyTripPlace?.id ?? null)) ||
      (input.insurances !== undefined &&
        InsuranceProperties.haveInsurancesChanged(
          proposition.insurances,
          input.insurances.map((insurance) => insurance.id)
        ))
    )
  }
}
